import FlexibleUmbPage from "#/_packages/umbrella/integrations/umbraco/components/pages/flexible-umb-page.tsx";
import PageNotFoundUmbPage from "#/_packages/umbrella/integrations/umbraco/components/pages/page-not-found-umb-page.tsx";
import StandardUmbPage from "#/_packages/umbrella/integrations/umbraco/components/pages/standard-umb-page.tsx";
import StartUmbPage from "#/_packages/umbrella/integrations/umbraco/components/pages/start-umb-page.tsx";
import { ContentTypes } from "#/_packages/umbrella/integrations/umbraco/utils/content-types.ts";
import {
	type DocTypeRendererMapping,
	type DocTypeRendererProps,
	useUmbracoRendererMappings,
} from "#/_packages/umbrella/integrations/umbraco/utils/renderer-mappings.tsx";
import {
	optionalPageModules,
	RendererModuleBoundary,
} from "./renderer-modules";

function DefaultDocTypeRenderer({
	content,
	breadcrumbs,
}: DocTypeRendererProps) {
	switch (content.contentType) {
		case ContentTypes.Error404Page:
			return <PageNotFoundUmbPage content={content} />;
		case ContentTypes.StartPage:
			return <StartUmbPage breadcrumbs={breadcrumbs} content={content} />;
		case ContentTypes.StandardPage:
			return <StandardUmbPage content={content} breadcrumbs={breadcrumbs} />;
		case ContentTypes.NewsListingPage:
			return (
				<optionalPageModules.newsListingUmbPage.Renderer
					content={content}
					breadcrumbs={breadcrumbs}
				/>
			);
		case ContentTypes.EventPage:
			return (
				<optionalPageModules.eventUmbPage.Renderer
					content={content}
					breadcrumbs={breadcrumbs}
				/>
			);
		case ContentTypes.EventListingPage:
			return (
				<optionalPageModules.eventListingUmbPage.Renderer
					content={content}
					breadcrumbs={breadcrumbs}
				/>
			);
		case ContentTypes.NewsPage:
			return (
				<optionalPageModules.newsUmbPage.Renderer
					content={content}
					breadcrumbs={breadcrumbs}
				/>
			);
		case ContentTypes.SearchPage:
			return (
				<optionalPageModules.searchUmbPage.Renderer
					content={content}
					breadcrumbs={breadcrumbs}
				/>
			);
		case ContentTypes.FlexiblePage:
			return <FlexibleUmbPage content={content} breadcrumbs={breadcrumbs} />;
		case ContentTypes.RestrictedAccessPage:
			return (
				<optionalPageModules.restrictedAccessUmbPage.Renderer
					content={content}
					breadcrumbs={breadcrumbs}
				/>
			);
		case ContentTypes.LoginPage:
			return (
				<optionalPageModules.loginUmbPage.Renderer
					content={content}
					breadcrumbs={breadcrumbs}
				/>
			);
		case ContentTypes.BlogStartPage:
			return (
				<optionalPageModules.blogStartUmbPage.Renderer
					content={content}
					breadcrumbs={breadcrumbs}
				/>
			);
		case ContentTypes.BlogPostPage:
			return (
				<optionalPageModules.blogPostUmbPage.Renderer
					content={content}
					breadcrumbs={breadcrumbs}
				/>
			);
		default:
			return null;
	}
}

export const defaultDocTypeRenderers: DocTypeRendererMapping =
	Object.fromEntries(
		[
			"error404UmbPage",
			"startUmbPage",
			"standardUmbPage",
			"newsListingUmbPage",
			"newsUmbPage",
			"eventUmbPage",
			"eventListingUmbPage",
			"searchUmbPage",
			"flexibleUmbPage",
			"restrictedAccessUmbPage",
			"loginUmbPage",
			"blogStartUmbPage",
			"blogPostUmbPage",
		].map((contentType) => [contentType, DefaultDocTypeRenderer]),
	);

export default function DocTypeRenderer({
	content,
	breadcrumbs,
}: DocTypeRendererProps) {
	const { pages } = useUmbracoRendererMappings();
	const CustomRenderer = pages[content.contentType];
	if (CustomRenderer)
		return <CustomRenderer content={content} breadcrumbs={breadcrumbs} />;
	const Renderer = defaultDocTypeRenderers[content.contentType];

	if (!Renderer) {
		return (
			<div>
				Failed to find view for page: <b>{content.contentType}</b>
			</div>
		);
	}

	return (
		<RendererModuleBoundary value={content}>
			<Renderer content={content} breadcrumbs={breadcrumbs} />
		</RendererModuleBoundary>
	);
}
